import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaCheck } from "react-icons/fa";

const DEFAULT_POINTS = [
  "Programme & project delivery leadership",
  "Enterprise integration and cloud modernization",
  "Regulated-industry experience end to end",
];

/**
 * Split shell shared by Login and Signup: a navy brand panel beside the
 * form card. Both pages previously duplicated the same centred-card markup.
 *
 * The brand copy is a prop rather than a constant so the two pages read
 * differently — signing in is a return, signing up is an arrival, and the
 * panel should say so.
 */
function AuthLayout({
  title,
  subtitle,
  icon,
  children,
  footer,
  eyebrow,
  brandTitle = "Technology, transformation & digital excellence.",
  brandLead = "Sign in to manage your account and the team board.",
  points = DEFAULT_POINTS,
}) {
  return (
    <div className="auth">
      <style>{`
        .auth {
          --peach-deep: #ff6b35;
          --peach: #ff9066;
          --peach-soft: #f3c09f;
          --peach-tint: #ffe9e0;
          --peach-ink: #c2410c;
          /* Deeper than --peach-ink, for the two places peach text sits on a
             peach backdrop: #c2410c measures 4.43:1 on the tint chip (just
             under the 4.5 floor) and 2.17:1 on the page gradient. */
          --peach-ink-deep: #7c2d12;

          position: relative; overflow: hidden;
          min-height: 100vh; display: flex; align-items: center;
          padding: 40px 0;
          background:
            radial-gradient(40rem 24rem at 88% 6%, rgba(255,107,53,.28), transparent 62%),
            linear-gradient(135deg, #ff9066 0%, #f3c09f 46%, #fff8f5 100%);
        }

        /* --- Backdrop: drifting light behind the card --- */
        .auth__aura {
          position: absolute; border-radius: 50%; pointer-events: none;
          filter: blur(70px); opacity: .55;
        }
        .auth__aura--a {
          width: 30rem; height: 30rem; top: -9rem; left: -7rem;
          background: radial-gradient(circle, rgba(255,107,53,.85), transparent 68%);
          animation: authDriftA 19s ease-in-out infinite;
        }
        .auth__aura--b {
          width: 26rem; height: 26rem; bottom: -10rem; right: -6rem;
          background: radial-gradient(circle, rgba(255,144,102,.8), transparent 68%);
          animation: authDriftB 23s ease-in-out infinite;
        }
        .auth__aura--c {
          width: 20rem; height: 20rem; top: 44%; right: 22%;
          background: radial-gradient(circle, rgba(255,233,224,.9), transparent 66%);
          animation: authDriftA 27s ease-in-out infinite reverse;
        }
        @keyframes authDriftA {
          0%, 100% { transform: translate3d(0,0,0) scale(1); }
          50%      { transform: translate3d(46px,32px,0) scale(1.12); }
        }
        @keyframes authDriftB {
          0%, 100% { transform: translate3d(0,0,0) scale(1); }
          50%      { transform: translate3d(-40px,-30px,0) scale(1.1); }
        }

        /* Faint dot field for texture — sits above the aura, below the card */
        .auth__grid {
          position: absolute; inset: 0; pointer-events: none;
          background-image: radial-gradient(rgba(15,23,42,.13) 1px, transparent 1px);
          background-size: 26px 26px;
          -webkit-mask-image: radial-gradient(70% 60% at 50% 45%, #000, transparent 78%);
          mask-image: radial-gradient(70% 60% at 50% 45%, #000, transparent 78%);
          opacity: .5;
        }

        .auth__inner { position: relative; z-index: 1; }

        .auth-shell {
          border-radius: 26px; overflow: hidden;
          box-shadow:
            0 34px 80px rgba(15,23,42,.32),
            0 0 0 1px rgba(255,255,255,.5);
          background: #fff;
          animation: authRise .6s cubic-bezier(.22,1,.36,1) both;
        }
        @keyframes authRise {
          from { opacity: 0; transform: translateY(20px) scale(.985); }
          to   { opacity: 1; transform: none; }
        }

        /* --- Brand panel --- */
        .auth-brand {
          position: relative; overflow: hidden; height: 100%;
          padding: clamp(30px, 4vw, 46px); color: #fff;
          background:
            radial-gradient(24rem 16rem at 88% 6%, rgba(255,107,53,.32), transparent 62%),
            linear-gradient(150deg, #131f36 0%, #0f172a 55%, #0b1120 100%);
        }
        /* Slow sheen so the navy panel is not a dead rectangle */
        .auth-brand::after {
          content: ""; position: absolute; inset: -40% -60%;
          background: linear-gradient(115deg, transparent 42%, rgba(255,144,102,.14) 50%, transparent 58%);
          animation: authSheen 9s ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes authSheen {
          0%, 100% { transform: translateX(-28%); }
          50%      { transform: translateX(28%); }
        }
        .auth-brand > * { position: relative; z-index: 1; }

        .auth-brand__logo {
          font-weight: 800; letter-spacing: 1px; font-size: 1.45rem; color: #fff;
          text-decoration: none; display: inline-block;
        }
        .auth-brand__logo span {
          background: linear-gradient(105deg, var(--peach-soft), var(--peach));
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .auth-brand__h {
          margin-top: 28px; font-weight: 800; letter-spacing: -.02em;
          font-size: clamp(1.3rem, 2.4vw, 1.75rem); line-height: 1.25;
        }
        .auth-brand__p { color: #cbd5e1; line-height: 1.8; margin-top: 12px; font-size: .94rem; }
        /* Block, or the span stays inline and drops both width and height */
        .auth-brand__rule {
          display: block;
          height: 3px; width: 54px; border-radius: 3px; margin-top: 22px;
          background: linear-gradient(90deg, var(--peach-deep), var(--peach));
        }
        .auth-point {
          display: flex; align-items: flex-start; gap: 11px;
          color: #cbd5e1; font-size: .9rem; margin-top: 14px;
        }
        .auth-tick {
          flex-shrink: 0; margin-top: 2px;
          width: 20px; height: 20px; border-radius: 50%;
          display: grid; place-items: center; font-size: .54rem; color: #fff;
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
        }
        .auth-back {
          display: inline-flex; align-items: center; gap: 8px; margin-top: 30px;
          color: var(--peach-soft); text-decoration: none;
          font-weight: 600; font-size: .88rem;
          transition: color .2s ease, transform .2s ease;
        }
        .auth-back:hover { color: var(--peach); transform: translateX(-3px); }

        /* --- Mobile brand header --- */
        /* The split panel is hidden below lg, which left phones with an
           unbranded card floating on a gradient. This puts the name back. */
        .auth-mobile {
          display: flex; align-items: center; justify-content: space-between;
          gap: 16px; margin-bottom: 18px; padding: 0 4px;
        }
        .auth-mobile__logo {
          font-weight: 800; letter-spacing: 1px; font-size: 1.3rem;
          color: #0f172a; text-decoration: none;
        }
        /* The wordmark sits straight on the page gradient with no panel behind
           it, so it needs the deep ink: navy "SARVAV" is 8:1, and this carries
           the accent at 4.2:1 against the lightest point of the gradient. */
        .auth-mobile__logo span { color: var(--peach-ink-deep); }
        .auth-mobile__back {
          display: inline-flex; align-items: center; gap: 7px;
          color: #0f172a; text-decoration: none;
          font-weight: 700; font-size: .84rem;
          background: rgba(255,255,255,.62);
          border: 1px solid rgba(255,255,255,.8);
          border-radius: 999px; padding: 7px 14px;
          transition: background .2s ease;
        }
        .auth-mobile__back:hover, .auth-mobile__back:focus-visible {
          background: #fff; color: #0f172a;
        }

        /* --- Form panel --- */
        .auth-form { padding: clamp(30px, 4vw, 48px); }
        .auth-form__eyebrow {
          display: inline-block; margin-bottom: 14px;
          font-size: .7rem; font-weight: 800; letter-spacing: .14em;
          text-transform: uppercase; color: var(--peach-ink-deep);
          background: var(--peach-tint); border-radius: 999px; padding: 6px 12px;
        }
        .auth-form__icon {
          width: 62px; height: 62px; border-radius: 18px; margin-bottom: 16px;
          display: grid; place-items: center; font-size: 1.5rem; color: #fff;
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
          box-shadow: 0 12px 26px rgba(255,107,53,.36);
        }
        .auth-form__h {
          color: #0f172a; font-weight: 800; font-size: 1.5rem;
          letter-spacing: -.02em; margin-bottom: 4px;
        }
        .auth-form__sub { color: #64748b; font-size: .94rem; margin-bottom: 24px; }
        .auth-form .form-label {
          font-weight: 600; font-size: .86rem; color: #334155; margin-bottom: 6px;
        }
        .auth-form .form-control {
          border: 1px solid #e6dbd4; border-radius: 12px;
          padding: 11px 14px; font-size: .95rem; background: #fffdfc;
          transition: border-color .2s ease, box-shadow .2s ease;
        }
        .auth-form .form-control:focus {
          border-color: var(--peach);
          box-shadow: 0 0 0 4px rgba(255,144,102,.22);
        }

        /* --- Field with leading icon / reveal toggle --- */
        .auth-field { position: relative; }
        .auth-field__icon {
          position: absolute; left: 14px; top: 50%; transform: translateY(-50%);
          color: #b9a79d; font-size: .88rem; pointer-events: none; z-index: 2;
        }
        .auth-field .form-control { padding-left: 40px; }
        .auth-field--pw .form-control { padding-right: 44px; }
        .auth-toggle {
          position: absolute; right: 5px; top: 50%; transform: translateY(-50%);
          width: 34px; height: 34px; border-radius: 9px;
          display: grid; place-items: center;
          background: transparent; border: none; padding: 0;
          color: #64748b; font-size: .88rem; cursor: pointer;
          transition: background .18s ease, color .18s ease;
        }
        .auth-toggle:hover { background: var(--peach-tint); color: var(--peach-ink); }
        .auth-toggle:focus-visible {
          outline: 2px solid var(--peach-deep); outline-offset: 1px;
        }

        .auth-caps {
          display: flex; align-items: center; gap: 7px;
          margin: 7px 0 0; font-size: .8rem; font-weight: 600;
          color: #b45309;
        }

        /* --- Password strength --- */
        .auth-strength { margin-top: 10px; }
        .auth-strength__bars { display: flex; gap: 5px; }
        .auth-strength__bars span {
          height: 4px; flex: 1; border-radius: 3px; background: #ece2dc;
          transition: background .25s ease;
        }
        .auth-strength__label {
          margin: 7px 0 0; font-size: .78rem; color: #64748b;
        }
        .auth-strength__label strong { color: #334155; }
        .auth-strength.is-weak   .is-on { background: #dc2626; }
        .auth-strength.is-fair   .is-on { background: #d97706; }
        .auth-strength.is-good   .is-on { background: #0d9488; }
        .auth-strength.is-strong .is-on { background: #16a34a; }
        .auth-strength.is-weak   .auth-strength__label strong { color: #b91c1c; }
        .auth-strength.is-fair   .auth-strength__label strong { color: #b45309; }
        .auth-strength.is-good   .auth-strength__label strong { color: #0f766e; }
        .auth-strength.is-strong .auth-strength__label strong { color: #15803d; }

        /* Live confirmation that two password fields agree */
        .auth-match {
          display: flex; align-items: center; gap: 7px;
          margin: 8px 0 0; font-size: .8rem; font-weight: 600;
        }
        .auth-match.is-ok   { color: #15803d; }
        .auth-match.is-bad  { color: #b91c1c; }

        .auth-submit {
          width: 100%; border: none; border-radius: 13px;
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
          color: #0f172a; font-weight: 700; padding: 13px; font-size: 1rem;
          box-shadow: 0 12px 26px rgba(255,107,53,.36);
          transition: transform .22s ease, box-shadow .22s ease, opacity .2s ease;
        }
        .auth-submit:hover:not(:disabled) {
          transform: translateY(-2px); box-shadow: 0 18px 34px rgba(255,107,53,.5);
        }
        .auth-submit:disabled { opacity: .65; cursor: not-allowed; }
        .auth-alt { text-align: center; color: #64748b; margin: 22px 0 0; font-size: .93rem; }
        .auth-alt a { color: var(--peach-ink); font-weight: 700; text-decoration: none; }
        .auth-alt a:hover { text-decoration: underline; }

        @media (prefers-reduced-motion: reduce) {
          .auth-submit, .auth-back { transition: none; }
          .auth-submit:hover, .auth-back:hover { transform: none; }
          .auth-shell { animation: none; }
          .auth__aura, .auth-brand::after { animation: none; }
        }
      `}</style>

      <span className="auth__aura auth__aura--a" aria-hidden="true" />
      <span className="auth__aura auth__aura--b" aria-hidden="true" />
      <span className="auth__aura auth__aura--c" aria-hidden="true" />
      <span className="auth__grid" aria-hidden="true" />

      <Container className="auth__inner" style={{ maxWidth: "960px" }}>
        {/* Stands in for the brand panel, which is hidden below lg */}
        <div className="auth-mobile d-lg-none">
          <Link to="/" className="auth-mobile__logo">
            SARVAV<span>YA</span>
          </Link>
          <Link to="/" className="auth-mobile__back">
            <FaArrowLeft aria-hidden="true" />
            Back to site
          </Link>
        </div>

        <Row className="g-0 auth-shell">
          {/* Hidden below lg — on a phone it would push the form off-screen */}
          <Col lg={5} className="d-none d-lg-block">
            <div className="auth-brand">
              <Link to="/" className="auth-brand__logo">
                SARVAV<span>YA</span>
              </Link>

              <h2 className="auth-brand__h">{brandTitle}</h2>
              <p className="auth-brand__p">{brandLead}</p>
              <span className="auth-brand__rule" />

              <div className="auth-brand__points">
                {points.map((p) => (
                  <div className="auth-point" key={p}>
                    <span className="auth-tick">
                      <FaCheck />
                    </span>
                    {p}
                  </div>
                ))}
              </div>

              <Link to="/" className="auth-back">
                <FaArrowLeft />
                Back to site
              </Link>
            </div>
          </Col>

          <Col lg={7}>
            <div className="auth-form">
              {eyebrow && <span className="auth-form__eyebrow">{eyebrow}</span>}
              <span className="auth-form__icon">{icon}</span>
              <h1 className="auth-form__h">{title}</h1>
              <p className="auth-form__sub">{subtitle}</p>

              {children}

              {footer && <p className="auth-alt">{footer}</p>}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default AuthLayout;
